import { Trash } from "@phosphor-icons/react";

import { Button } from "common/components/ui/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import type { LinkedInProfile } from "common/types";

import useAudienceMutations from "../../../datahooks/useAudienceMutations";

import type { AudienceFilters } from "../../../types";

interface RemoveTargetsProps {
  filters: AudienceFilters;
  hasSelectedProfiles: boolean;
  isMiningTargets: boolean;
  selectedProfiles: LinkedInProfile[];
  profilesCount: number;
  resetAllFilters: () => void;
  resetSelectedProfiles: () => void;
}

export default function RemoveTargets({
  filters,
  hasSelectedProfiles,
  isMiningTargets,
  selectedProfiles,
  profilesCount,
  resetAllFilters,
  resetSelectedProfiles,
}: RemoveTargetsProps) {
  const { removeProfiles, isRemovingProfiles } = useAudienceMutations(filters);

  function handleRemoveTargets() {
    if (hasSelectedProfiles) {
      const targetUrns = selectedProfiles.map((profile) => profile.urn);

      removeProfiles({ targetUrns }).then(() => {
        // if all profiles are removed, reset filters
        if (targetUrns.length === profilesCount) {
          resetAllFilters();
        }

        resetSelectedProfiles();
      });
    } else {
      removeProfiles({ filters }).then(() => {
        resetAllFilters();
      });
    }
  }

  const removeTargetsButton = (
    <Button
      className="max-md:flex-1"
      variant="outline-danger"
      leftIcon={<Trash />}
      disabled={isRemovingProfiles || isMiningTargets || profilesCount === 0}
      onClick={handleRemoveTargets}
    >
      <span className="truncate">
        {hasSelectedProfiles
          ? `Remove ${selectedProfiles.length} Target${selectedProfiles.length > 1 ? "s" : ""}`
          : "Remove targets"}
      </span>
    </Button>
  );

  if (isMiningTargets) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{removeTargetsButton}</TooltipTrigger>
        <TooltipContent>
          You will be able to remove your Audience after all targets are loaded
        </TooltipContent>
      </Tooltip>
    );
  }

  return removeTargetsButton;
}
